import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, XCircle, Mail } from "lucide-react";
import axios, { getApiUrl } from "@/lib/api";

const API = `${getApiUrl()}/api`;

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [status, setStatus] = useState("loading"); // loading, success, error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Invalid verification link. Please check your email for the correct link.");
      return;
    }

    verifyEmail();
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await axios.get(`${API}/auth/verify-email?token=${token}`);
      setStatus("success");
      setMessage(response.data.message || "Email verified successfully!");
    } catch (error) {
      setStatus("error");
      setMessage(error.response?.data?.detail || "Verification failed. The link may have expired.");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <div className="w-full max-w-md text-center">
        {/* Logo */}
        <div className="inline-flex items-center justify-center mb-6">
          <img src="/logo.png" alt="Game-o-Rang" className="w-24 h-24 object-contain" />
        </div>

        {/* Status Icon */}
        <div className="inline-flex items-center justify-center mb-6">
          {status === "loading" && (
            <div className="w-16 h-16 rounded-full bg-purple-500/10 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
            </div>
          )}
          {status === "success" && (
            <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          )}
          {status === "error" && (
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center">
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
          )}
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-white mb-4">
          {status === "loading" && "Verifying Email..."}
          {status === "success" && "Email Verified!"}
          {status === "error" && "Verification Failed"}
        </h1>

        {/* Message */}
        <p className="text-white/60 mb-8">{message}</p>

        {/* Actions */}
        <div className="space-y-4">
          {status === "success" && (
            <Link to="/login">
              <Button className="w-full bg-purple-600 hover:bg-purple-700">
                Go to Login
              </Button>
            </Link>
          )}
          
          {status === "error" && (
            <>
              <Link to="/login">
                <Button variant="outline" className="w-full bg-white/5 border-white/10 text-white hover:bg-white/10">
                  <Mail className="w-4 h-4 mr-2" />
                  Request New Verification Email
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="ghost" className="w-full text-purple-400 hover:text-purple-300">
                  Back to Login
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
